"use client";

import { Check, ChevronsUpDown, Loader2, Search } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { RegionFlag } from "@/components/region-flag";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { CitationStyleMeta } from "@/data/citation-styles";
import { cn } from "@/lib/utils";

interface StyleComboboxProps {
  value: string;
  onChange: (value: string) => void;
  /** Featured styles shown by default before search */
  featuredStyles: CitationStyleMeta[];
  /** Placeholder text */
  placeholder?: string;
  className?: string;
}

type SearchResult = CitationStyleMeta;

export function StyleCombobox({
  value,
  onChange,
  featuredStyles,
  placeholder = "Select style...",
  className,
}: StyleComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keep track of selected style even after search clears
  const [selectedStyleCache, setSelectedStyleCache] =
    useState<CitationStyleMeta | null>(null);

  // Find selected style from featured, search results, or cache
  const allKnownStyles = [...featuredStyles, ...searchResults];
  let selectedStyle = allKnownStyles.find((s) => s.slug === value);

  // If not found in current lists but we have it cached, use cache
  if (!selectedStyle && selectedStyleCache?.slug === value) {
    selectedStyle = selectedStyleCache;
  }

  // Debounced search
  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const res = await fetch(
        `/api/search-styles?q=${encodeURIComponent(query)}`
      );
      const data = await res.json();
      setSearchResults(data.results ?? []);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (search.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(search);
      }, 300);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search, handleSearch]);

  // Styles to display: search results if searching, otherwise featured
  const displayStyles = search.trim() ? searchResults : featuredStyles;

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          role="combobox"
          variant="outline"
        >
          <span className="flex items-center gap-2 truncate">
            {selectedStyle ? (
              <>
                <RegionFlag region={selectedStyle.region} size="sm" />
                <span className="truncate">
                  {selectedStyle.shortName ?? selectedStyle.name}
                </span>
              </>
            ) : (
              placeholder
            )}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-0">
        <div className="border-border border-b p-2">
          <div className="relative">
            <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              className="h-9 pl-8"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search 10,000+ styles..."
              value={search}
            />
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto p-1">
          {isSearching && (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="mr-2 size-4 animate-spin" />
              <span className="text-sm">Searching...</span>
            </div>
          )}
          {!isSearching && displayStyles.length === 0 && (
            <div className="py-6 text-center text-muted-foreground text-sm">
              {search.trim() ? "No styles found." : "Start typing to search..."}
            </div>
          )}
          {!isSearching && displayStyles.length > 0 && (
            <>
              {!search.trim() && (
                <div className="px-2 py-1.5 font-medium text-muted-foreground text-xs">
                  Popular styles
                </div>
              )}
              {displayStyles.map((style) => (
                <button
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors",
                    "hover:bg-accent focus:bg-accent focus:outline-none",
                    value === style.slug && "bg-accent"
                  )}
                  key={style.slug}
                  onClick={() => {
                    onChange(style.slug);
                    setSelectedStyleCache(style); // Cache for display after search clears
                    setOpen(false);
                    setSearch("");
                  }}
                  type="button"
                >
                  <Check
                    className={cn(
                      "size-4 shrink-0",
                      value === style.slug ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <RegionFlag region={style.region} size="sm" />
                  <span className="truncate">
                    {style.shortName ?? style.name}
                  </span>
                </button>
              ))}
              {search.trim() && searchResults.length > 0 && (
                <div className="mt-1 border-t px-2 py-1.5 pt-2 text-muted-foreground text-xs">
                  {searchResults.length} result
                  {searchResults.length !== 1 ? "s" : ""} found
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
